import { useEffect, useRef } from "react";

/**
 * A hook to synchronize overlay components (sheets/modals) with browser history,
 * allowing users to close them using the hardware back button or back swipe gesture.
 *
 * @param {boolean} isOpen - Whether the overlay is currently open.
 * @param {function} onClose - Callback to close the overlay.
 * @param {string} stateName - A descriptive name for the overlay state.
 */
export const useModalHistory = (isOpen, onClose, stateName) => {
    // Keep the latest onClose in a ref instead of the effect's dependency
    // array. Callers often pass an inline arrow function (a new reference
    // every render), which used to force this effect to tear down and
    // re-run on every re-render while the modal was open — not just on
    // actual open/close. That teardown/rebuild raced push/pop of the
    // history stack and could produce a stray popstate that closed the
    // modal immediately after opening it (e.g. tapping a Routine Care
    // category card triggered a re-render, which retriggered this effect,
    // which closed the whole popup instantly).
    const onCloseRef = useRef(onClose);
    useEffect(() => {
        onCloseRef.current = onClose;
    }, [onClose]);

    useEffect(() => {
        if (!isOpen) return;

        const timestamp = Date.now();
        const stateId = `modal-${stateName}-${timestamp}`;

        // Push a dummy state onto history
        window.history.pushState({ modalId: stateId }, "");

        const handlePopState = () => {
            const currentState = window.history.state;

            // If popped to a non-modal state (root page)
            if (!currentState?.modalId) {
                onCloseRef.current();
                return;
            }

            // Extract the timestamp of the new state
            const currentTimestamp = parseInt(currentState.modalId.split("-").pop() || "0", 10);

            // If the popped state is older than this modal, close it
            if (currentTimestamp < timestamp) {
                onCloseRef.current();
            }
        };

        window.addEventListener("popstate", handlePopState);

        return () => {
            window.removeEventListener("popstate", handlePopState);

            // If the overlay was closed programmatically (not via back swipe/button),
            // remove our dummy state from history to prevent ghost navigation steps.
            if (window.history.state?.modalId === stateId) {
                window.history.back();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, stateName]);
};
